import ReactPlayerLoader from "@brightcove/react-player-loader";
import { BC_ACCOUNT_ID, BC_PLAYER_ID } from "@helper/getEnvVariables";
import cx from "classnames";
import { ReactElement, RefObject, useCallback, useEffect, useRef } from "react";

import styles from "./BrightcoveVideo.module.css";

interface VideoPlayerRef {
  on(event: string, callback: () => void): void;
  el(): HTMLElement;
  play(): Promise<void>;
  pause(): void;
  paused(): boolean;
  muted(): boolean;
  muted(value: boolean): void;
}

type BrightcoveVideoProps = {
  videoId: string;
  forwardedRef?: RefObject<HTMLDivElement>;
  videoRef?: RefObject<HTMLDivElement>;
  onEnded?: () => void;
  nextPlaylist?: () => void;
  className?: string;
  playerId?: string;
  customButtons?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  isMute?: boolean;
  setIsMute: (mute: boolean) => void;
};

export function QnBrightcoveVideo({
  videoId,
  forwardedRef,
  videoRef,
  onEnded,
  nextPlaylist,
  className,
  playerId = BC_PLAYER_ID,
  customButtons = false,
  onPlay,
  onPause,
  isMute = false,
  setIsMute,
}: BrightcoveVideoProps): ReactElement {
  const playerRef = useRef<VideoPlayerRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  /* ---------------------------------------------------------
   * Handle successful Brightcove player load
   * --------------------------------------------------------- */
  const handleSuccess = useCallback(
    ({ ref }: { ref: VideoPlayerRef }) => {
      playerRef.current = ref;

      // Hide play button.
      const playButton = ref.el().querySelector(".vjs-big-play-button");
      if (playButton instanceof HTMLElement)
        playButton.style.visibility = "hidden";

      ref.muted(isMute);
      ref
        .play()
        .then(() => {
          if (!isMute) setIsMute(false);
        })
        .catch(() => {
          ref.muted(true);
          void ref.play();
          setIsMute(true);
        });

      const onVolumeChange = () => {
        const muted = ref.muted();

        setIsMute(muted);
      };

      ref.on("volumechange", onVolumeChange);

      // Attach player event listeners
      if (onEnded) ref.on("ended", onEnded);
      if (onPlay) ref.on("play", onPlay);
      if (onPause) ref.on("pause", onPause);

      // Bind next playlist click if needed
      if (nextPlaylist) {
        const nextButton = ref.el().querySelector(".vjs-next-button");
        if (nextButton) {
          const onNextClick = () => {
            nextPlaylist();
          };

          nextButton.addEventListener("click", onNextClick);

          // Cleanup when player reloads or unmounts
          return () => {
            nextButton.removeEventListener("click", onNextClick);
          };
        }
      }
    },
    [isMute, onEnded, onPlay, onPause, nextPlaylist, setIsMute],
  );

  /* ---------------------------------------------------------
   * Play / Pause toggle
   * --------------------------------------------------------- */
  const togglePlay = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    if (player.paused()) void player.play();
    else player.pause();
  }, []);

  /* ---------------------------------------------------------
   * Keep player mute state in sync
   * --------------------------------------------------------- */
  useEffect(() => {
    playerRef.current?.muted(isMute);
  }, [isMute]);

  /* ---------------------------------------------------------
   * Auto play/pause based on visibility
   * --------------------------------------------------------- */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const player = playerRef.current;
        if (!player) return;

        entry.isIntersecting ? void player.play() : player.pause();
      },
      { threshold: 0.35 },
    );

    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <div
        key={videoId}
        data-testid={videoId}
        className={cx(styles.brightcove, className)}
        ref={forwardedRef}
      >
        <ReactPlayerLoader
          style={{ gridColumn: 1, gridRow: 1 }}
          accountId={BC_ACCOUNT_ID}
          playerId={playerId}
          videoId={videoId}
          videoRef={videoRef}
          onSuccess={handleSuccess}
          playsinline // Support for ios to autoplay
        />
      </div>

      {customButtons && (
        <div
          ref={containerRef}
          className="absolute inset-0 z-10 overflow-hidden rounded-3xl"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.8) 100%)",
          }}
        >
          <button
            onClick={togglePlay}
            className="w-full h-full rounded play-video"
            aria-label="Toggle Play"
          />
        </div>
      )}
    </>
  );
}
